/**
 * Бот, собранный в SPECTER.
 *
 * Четвёртая сущность платформы, рядом с индикатором, правилом и внешним
 * ботом. От правила отличается тем, что не только сообщает: у него есть
 * действия, ограничения риска и состояние. От внешнего бота — тем, что
 * его никто не программировал: он собран из готовых модулей и целиком
 * описывается этой структурой.
 *
 * Три способа создания дают одну и ту же структуру:
 *
 *   шаблон ──┐
 *   конструктор ──┼──► BotDefinition ──► исполнение
 *   внешний ──┘
 *
 * Шаблон — не отдельная ветка в интерфейсе с собственной логикой, а
 * заготовка, разворачивающаяся в те же модули. Иначе «Grid Bot» стал бы
 * магией, которую нельзя ни посмотреть, ни поправить, ни повторить
 * вручную.
 *
 * **Условия — те же, что у правил.** `RuleGroup` переиспользуется целиком:
 * второй язык условий означал бы второй вычислитель и неизбежное
 * расхождение между «правило сработало» и «бот сработал» на одних и тех
 * же данных.
 */
export const MAX_BOT_LEVERAGE = 50;
/** Как торгует бот — из профиля; непонятное приводится к споту. */
export function botTradeFrom(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const value = raw;
    if (value['market'] !== 'futures')
        return { market: 'spot', direction: 'long', leverage: 1 };
    const leverage = typeof value['leverage'] === 'number' && Number.isFinite(value['leverage']) ? value['leverage'] : 1;
    return {
        market: 'futures',
        direction: value['direction'] === 'short' ? 'short' : value['direction'] === 'both' ? 'both' : 'long',
        leverage: Math.min(MAX_BOT_LEVERAGE, Math.max(1, Math.round(leverage))),
    };
}
/** Чьи сигналы слушает бот: `Signal.botId` источника. */
export function botSourceId(source) {
    if (source.kind === 'signal')
        return source.specId;
    if (source.kind === 'radar')
        return radarSourceId(source.radarId);
    return source.watchId;
}
/** `Signal.botId` событий своего радара. */
export function radarSourceId(radarId) {
    return `radar:${radarId}`;
}
/** Источник из профиля; непонятная запись — `null`, а не бот без решений. */
export function botSourceFrom(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const value = raw;
    if (value['kind'] === 'signal' && typeof value['specId'] === 'string' && value['specId'] !== '') {
        return { kind: 'signal', specId: value['specId'] };
    }
    if (value['kind'] === 'indicator' && typeof value['watchId'] === 'string' && value['watchId'] !== '') {
        return {
            kind: 'indicator',
            watchId: value['watchId'],
            ...(typeof value['title'] === 'string' ? { title: value['title'] } : {}),
        };
    }
    if (value['kind'] === 'radar' && typeof value['radarId'] === 'string' && value['radarId'] !== '') {
        return { kind: 'radar', radarId: value['radarId'], ...(typeof value['title'] === 'string' ? { title: value['title'] } : {}) };
    }
    return null;
}
const SIDES = ['LONG', 'SHORT', 'ALERT'];
/**
 * Индикатор бота из профиля. Проверяется форма, а не смысл: смысл
 * описания разбирает тот же код, что читает индикаторы, — на сервере и в
 * терминале свой.
 */
export function botSignalFrom(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const value = raw;
    const spec = value['spec'];
    if (typeof spec !== 'object' || spec === null || !Array.isArray(spec['branches']) || typeof spec['id'] !== 'string')
        return null;
    const side = (entry) => (typeof entry === 'string' && SIDES.includes(entry) ? entry : null);
    const entry = side(value['entry']);
    const exit = side(value['exit']);
    if (entry === null && exit === null)
        return null;
    const specId = typeof value['specId'] === 'string' && value['specId'] !== '' ? value['specId'] : null;
    return { spec: spec, entry, exit, ...(specId ? { specId } : {}), ...(value['usePlan'] === true ? { usePlan: true } : {}) };
}
/** Цвет состояния — один на всю платформу. */
export const BOT_STATE_TONE = {
    draft: 'idle',
    stopped: 'idle',
    starting: 'warn',
    running: 'live',
    paused: 'warn',
    error: 'bad',
};
//# sourceMappingURL=bot-definition.js.map